import { Component, ElementRef, EventEmitter, Input, Output,
  AfterViewInit, ViewChild, Renderer, ContentChild, AfterContentInit, forwardRef } from '@angular/core';
import { FormControl, ControlValueAccessor, FormControlDirective, FormControlName, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PuiInputType } from './input-config';

let nextId = 0;

export class PuiInputChange {
  source: PuiInputComponent;
  value?: any;
}

export const PUI_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiInputComponent),
  multi: true
};

@Component({
  // changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'puiInput',
  selector: 'pui-input',
  providers: [PUI_INPUT_CONTROL_VALUE_ACCESSOR],
  templateUrl: 'input.component.html'
})
export class PuiInputComponent implements AfterContentInit, AfterViewInit, ControlValueAccessor {
  static whitelist = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Delete', 'Backspace'];

  @Input() id = `pui-input-${nextId++}`;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() type: PuiInputType = PuiInputType.Text;
  @Input() icon: string;
  @Input() disabled: boolean;
  // preemptive behavior different from validations
  // @Input() maxLength: number;
  // only for number type
  @Input() step: number;
  @Input() integerOnly: boolean;
  @Input() positiveOnly: boolean;

  @Output() change: EventEmitter<PuiInputChange> = new EventEmitter<PuiInputChange>();

  @ViewChild('input') input: ElementRef;

  _control: FormControl = new FormControl();
  private innerValue: any;
  @ContentChild(FormControlName) controlName: FormControlDirective;
  @ContentChild(FormControlDirective) controlDirective: FormControlDirective;

  constructor(private renderer: Renderer) { }

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  isHiddenType(): boolean {
    return this.type === PuiInputType.Hidden;
  }

  get value() {
    return this.innerValue;
  }

  set value(val: any) {
    if (val !== this.innerValue) {
      this.innerValue = val ? val : (PuiInputType.Number === this.type ? val * 1 : val);
      this.onChangeCallback(this.innerValue);
      this.change.emit(<PuiInputChange>{
        source: this,
        value: this.innerValue
      });
    }
  }

  writeValue(v: any): void {
    if (v !== this.innerValue) {
      this.innerValue = v;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  _onBlur() {
    this.onTouchedCallback();
  }

  ngAfterContentInit() {
    if (this.controlDirective) {
      this._control = this.controlDirective.control;
    } else if (this.controlName) {
      this._control = this.controlName.control;
    }
    if (this._control.disabled) {
      this.disabled = true;
    }
  }

  ngAfterViewInit() {
    if (PuiInputType.Number === this.type) {
      let validators: Array<((val: any) => boolean)> = [];
      if (this.integerOnly) {
        validators.push((val) => {
          return '-' === val || Number.isInteger(val * 1);
        });
      }
      if (this.positiveOnly) {
        validators.push((val) => {
          return (val * 1) > 0;
        });
      }
      validators.push((val) => {
        return /^-?\d*(\.)?\d*$/.test(val);
      });
      this.renderer.listen(this.input.nativeElement, 'keydown', (e: KeyboardEvent) => {
        let currentVal = this.innerValue ? this.innerValue : '';
        let val = currentVal + e.key; // TODO: depending on the caret position
        if ((PuiInputComponent.whitelist.indexOf(e.code) < 0 && !(validators.every(v => v.apply(this, [val]))))
          || (e.code === 'Period' && (this.integerOnly || currentVal.indexOf('.') >= 0))) {
          this._ignore(e);
        }
      });
    }
  }

  _onChange(e: any) {
    // will be captured & emit on date entered/picked
    e.stopPropagation();
  }

  _ignore(event: any): void {
    event.preventDefault();
  }
}
